// src/features/variant_workflow/clinical_observation/hooks/useReportPanelModalLogic.ts
import { notification } from 'antd';
import type { TransferItem } from 'antd/es/transfer';
import { useMemo } from 'react';
import { useParams } from 'react-router';

import { useModalForm } from '../../../../foundation/hooks';
import { displayUnAuthorizedNotification } from '../../../../foundation/utils/notifications';
import {
  useGetMedicalInvestigationsForVariantQuery,
  useGetVariantDetailQuery,
  useUpdateReportVariantMutation,
} from '../../../api/variantApi';
import { NOTIFICATION_MESSAGES, VARIANT_STAGE_TYPES } from '../../constants';
import { VariantSample } from '../../redux';

// Prepare data for Transfer
interface PanelTransferItem extends TransferItem {
  key: string;
  title: string;
}

// Form values type for Formik
interface ReportPanelFormValues {
  selected_panel_keys: string[];
}

interface UseReportPanelModalLogicProps {
  closeModal: () => void;
  sample?: VariantSample;
}

/**
 * Custom hook that encapsulates all the business logic for ReportPanelModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useReportPanelModalLogic = ({
  closeModal,
  sample,
}: UseReportPanelModalLogicProps) => {
  const {
    isLoading,
    setIsLoading,
    user,
    stageInfo,
    getIsSaveDisabled,
    createModalOpts,
  } = useModalForm({
    onClose: closeModal,
    successMessage: NOTIFICATION_MESSAGES.REPORT_PANEL_UPDATED,
    stageType: VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION,
  });

  const { vid } = useParams();
  // Read the selected variant straight from the RTK Query cache (populated
  // by the workflow loader) rather than a Redux selector.
  const { data: variant } = useGetVariantDetailQuery(
    { variant_id: vid },
    { skip: !vid },
  );

  // Read the investigations list straight from the query hook's cache
  const { data: medicalInvestigations } =
    useGetMedicalInvestigationsForVariantQuery(
      { variant_id: variant?.id as number },
      { skip: !variant?.id },
    );

  // Mutation hook returns a [trigger, result] tuple. The endpoint already
  // invalidates VariantSamples, so the list refetches automatically on success
  const [updateReportVariantMutation] = useUpdateReportVariantMutation();

  const transferDataSource: PanelTransferItem[] = useMemo(
    () =>
      (medicalInvestigations ?? []).reduce(
        (acc: PanelTransferItem[], inv: { id: number; name: string }) => {
          if (inv.name && inv.name.trim()) {
            acc.push({
              key: inv.id.toString(),
              title: inv.name,
            });
          }
          return acc;
        },
        [] as PanelTransferItem[],
      ),
    [medicalInvestigations],
  );

  const initialValues: ReportPanelFormValues = useMemo(
    () => ({
      selected_panel_keys: sample?.report_data
        ? sample.report_data.map((rd: any) =>
            rd.medicalinvestigation_id.toString(),
          )
        : [],
    }),
    [sample],
  );

  const modalOpts = {
    ...createModalOpts(
      'Please select a diagnostic panel to report the variant.',
    ),
  };

  const handleFormSubmit = async (values: any) => {
    if (isLoading) {
      return;
    }

    if (user && sample?.sample_id && variant?.id) {
      try {
        setIsLoading(true);
        await updateReportVariantMutation({
          sample_id: String(sample.sample_id),
          genotype_id: String(sample.genotype_id),
          requestData: values.selected_panel_keys,
          variant_id: variant.id,
        }).unwrap();

        notification.success({
          message: NOTIFICATION_MESSAGES.SUCCESS,
          description: NOTIFICATION_MESSAGES.REPORT_PANEL_UPDATED,
        });
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
        closeModal();
      }
    } else {
      displayUnAuthorizedNotification();
      return;
    }
  };

  return {
    isLoading,
    user,
    variant,
    stageInfo,
    transferDataSource,
    modalOpts,
    initialValues,
    getIsSaveDisabled,
    handleFormSubmit,
  };
};

export default useReportPanelModalLogic;
